/**
 * Import from Google Drive, using the account already signed in.
 *
 * ## Why this is done through Firebase rather than the Google Picker
 *
 * The documented path for Drive-in-a-web-app is Google Identity Services plus the
 * Picker with the `drive.file` scope. It is the better design on paper — `drive.file`
 * grants access only to files the person explicitly picks, so the app never holds a
 * key to the whole Drive. It also could not be built here without console work: GIS
 * requires the app's exact origin listed under the OAuth client's authorised
 * JavaScript origins, and that client is Firebase-managed and only editable in the
 * console. Shipping a button that fails until somebody edits an OAuth client is
 * worse than shipping one that works.
 *
 * `reauthenticateWithPopup` sidesteps it entirely: the consent flow runs on
 * `steyn-fabric.firebaseapp.com`, which is already an authorised origin, and Firebase
 * hands back the Google access token from the exchange. No new client, no new origin,
 * nothing to configure.
 *
 * ## The cost of that choice, stated plainly
 *
 * It needs `drive.readonly`, which is a *sensitive* scope, so the first time anyone
 * connects, Google shows "Google hasn't verified this app" and they have to click
 * Advanced → Continue. That is a real wart. It is a one-time click for a household of
 * four, and the alternative was a button that does not work.
 *
 * Read-only is load-bearing, not incidental: Vault can list and copy from Drive and
 * has no ability to modify or delete anything there, so the worst case for a bug in
 * this file is a wasted download.
 *
 * ## Handling of the token
 *
 * Kept in a module-local variable and nowhere else. Not localStorage, not
 * sessionStorage, not a cookie — it is a bearer token for the person's entire Drive,
 * and it expires in about an hour. When it expires the next call 401s and we ask for
 * consent again, which is the correct behaviour rather than something to engineer
 * around.
 */
import { GoogleAuthProvider, reauthenticateWithPopup } from "firebase/auth";
import { fabricAuth } from "./auth";

/**
 * `drive.file`, deliberately, not `drive.readonly`.
 *
 * Two reasons, and they are the same reason.
 *
 * **Consent.** `drive.readonly` is a *restricted* scope — Google's most stringent
 * tier, because it reads the person's entire Drive. Publishing it means an annual
 * third-party CASA security assessment; without one, every sign-in shows "Google
 * hasn't verified this app" and warns about sensitive access. `drive.file` is
 * non-sensitive: no assessment, no warning, no verification at all.
 *
 * **Access.** `drive.file` grants access only to files the person explicitly
 * hands over through the Picker. Vault cannot see the rest of the Drive, so a
 * bug or a stolen token cannot reach it. For a document store that is the right
 * default even setting the consent screen aside.
 *
 * The cost, stated plainly: Vault can no longer enumerate a watched folder, so
 * the polled-scanner intake in 03-vault.md §4 becomes "pick the files" instead.
 * Files already picked stay reachable — the grant persists per file.
 */
const SCOPE = "https://www.googleapis.com/auth/drive.file";

/** Firebase's browser key doubles as the Picker developer key. */
const DEVELOPER_KEY: string = JSON.parse(import.meta.env.VITE_FABRIC_FIREBASE_CONFIG).apiKey;

let token: string | null = null;

export type DriveFile = {
  id: string;
  name: string;
  mimeType: string;
  /** Absent for Google-native files, which have no byte size until exported. */
  size: number | null;
  modifiedTime: string | null;
  iconLink: string | null;
  /** True for Docs/Sheets/Slides, which must be exported rather than downloaded. */
  native: boolean;
};

export class DriveError extends Error {}

export function isConnected(): boolean {
  return token !== null;
}

/** Drop the token — used by "Disconnect", and after a 401. */
export function forget(): void {
  token = null;
}

/**
 * Ask for Drive consent and keep the resulting access token.
 *
 * `reauthenticateWithPopup` rather than `signInWithPopup`: it is scoped to the
 * account already signed in, so there is no account chooser. On a shared desktop a
 * chooser here would let someone connect *their* Drive to the household's Vault
 * without it being obvious that the identity changed halfway through.
 */
export async function connect(): Promise<void> {
  const user = fabricAuth.currentUser;
  if (!user) throw new DriveError("Sign in first.");

  const provider = new GoogleAuthProvider();
  provider.addScope(SCOPE);

  let result;
  try {
    result = await reauthenticateWithPopup(user, provider);
  } catch (err) {
    const code = String((err as { code?: string }).code ?? "");
    // Closing the popup is a decision, not a fault. Saying "Drive failed" to
    // someone who changed their mind teaches them the feature is broken.
    if (code.includes("popup-closed-by-user") || code.includes("cancelled-popup-request")) {
      throw new DriveError("Cancelled.");
    }
    if (code.includes("popup-blocked")) {
      throw new DriveError("Your browser blocked the popup. Allow popups for this site and try again.");
    }
    throw new DriveError(`Could not connect to Drive (${code || "unknown"}).`);
  }

  const cred = GoogleAuthProvider.credentialFromResult(result);
  if (!cred?.accessToken) {
    throw new DriveError("Google did not return Drive access. Try again and accept the Drive permission.");
  }
  token = cred.accessToken;
}

// ---------------------------------------------------------------------------
// The Picker — Google's own file browser
// ---------------------------------------------------------------------------

/**
 * Loaded from Google's CDN on first use, not at boot.
 *
 * It is ~200 KB and most sessions never open the Picker at all, so paying for it
 * on every page load would be a straight tax on the review queue — the screen
 * that actually gets used daily.
 */
let pickerReady: Promise<void> | null = null;

function loadPicker(): Promise<void> {
  if (pickerReady) return pickerReady;
  pickerReady = new Promise<void>((resolve, reject) => {
    const g = (window as unknown as { gapi?: { load(m: string, cb: () => void): void } }).gapi;
    if (g) return g.load("picker", () => resolve());

    const s = document.createElement("script");
    s.src = "https://apis.google.com/js/api.js";
    s.async = true;
    s.onload = () => {
      const gapi = (window as unknown as { gapi: { load(m: string, cb: () => void): void } }).gapi;
      gapi.load("picker", () => resolve());
    };
    // Reset so a failure caused by a blocked CDN or a dropped connection can be
    // retried, rather than poisoning the promise for the rest of the session.
    s.onerror = () => {
      pickerReady = null;
      reject(new DriveError("Could not load Google's file browser. Check your connection and try again."));
    };
    document.head.appendChild(s);
  });
  return pickerReady;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type PickerGlobal = { picker: any };

/**
 * Open Drive's real folder browser and return whatever was chosen.
 *
 * This replaces listing the Drive ourselves, which is what `drive.file` makes
 * impossible and what produced the flat wall of unrelated files: with no folder
 * context, "newest 100 things in your Drive" is not a place anyone can navigate.
 * The Picker is Google's own UI — folders, breadcrumbs, search, shared drives,
 * Recent and Starred — and it is the *only* way a `drive.file` app can be handed
 * a file.
 *
 * Selecting a file here also grants Vault access to it. Nothing else.
 */
export async function pick(): Promise<DriveFile[]> {
  if (!token) throw new DriveError("Not connected to Drive.");
  await loadPicker();

  const google = (window as unknown as { google: PickerGlobal }).google;
  const P = google.picker;

  return new Promise<DriveFile[]>((resolve) => {
    // Two views: the whole Drive with folders, and shared drives. Folder
    // *selection* stays off — Vault files documents, and handing it a folder
    // would silently mean "and everything that ever lands in it", which is
    // exactly the open-ended access drive.file exists to avoid.
    const myDrive = new P.DocsView(P.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false);
    const shared = new P.DocsView(P.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setEnableDrives(true);

    const picker = new P.PickerBuilder()
      .setOAuthToken(token)
      .setDeveloperKey(DEVELOPER_KEY)
      .setTitle("Choose documents for the Vault")
      .addView(myDrive)
      .addView(shared)
      .addView(new P.DocsUploadView())
      .enableFeature(P.Feature.MULTISELECT_ENABLED)
      .setCallback((data: any) => {
        if (data[P.Response.ACTION] === P.Action.CANCEL) return resolve([]);
        if (data[P.Response.ACTION] !== P.Action.PICKED) return;

        const docs = (data[P.Response.DOCUMENTS] ?? []) as Record<string, any>[];
        resolve(
          docs.map((d) => {
            const mimeType = String(d[P.Document.MIME_TYPE] ?? "application/octet-stream");
            const size = d.sizeBytes ?? d[P.Document.SIZE_BYTES];
            return {
              id: String(d[P.Document.ID]),
              name: String(d[P.Document.NAME] ?? "Untitled"),
              mimeType,
              // Google-native files report no size until they are exported.
              size: size == null ? null : Number(size),
              modifiedTime: d[P.Document.LAST_EDITED_UTC]
                ? new Date(Number(d[P.Document.LAST_EDITED_UTC])).toISOString()
                : null,
              iconLink: (d[P.Document.ICON_URL] as string) ?? null,
              native: mimeType.startsWith("application/vnd.google-apps."),
            };
          }),
        );
      })
      .build();

    picker.setVisible(true);
  });
}
/* eslint-enable @typescript-eslint/no-explicit-any */

async function driveGet(path: string): Promise<Response> {
  if (!token) throw new DriveError("Not connected to Drive.");
  const res = await fetch(`https://www.googleapis.com/drive/v3/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401 || res.status === 403) {
    forget();
    throw new DriveError("Drive access expired. Connect again.");
  }
  if (!res.ok) throw new DriveError(`Drive returned ${res.status}.`);
  return res;
}

/** Google-native types and the format each exports to. */
const EXPORT_AS: Record<string, { mime: string; ext: string }> = {
  "application/vnd.google-apps.document": { mime: "application/pdf", ext: "pdf" },
  "application/vnd.google-apps.spreadsheet": { mime: "application/pdf", ext: "pdf" },
  "application/vnd.google-apps.presentation": { mime: "application/pdf", ext: "pdf" },
  "application/vnd.google-apps.drawing": { mime: "application/pdf", ext: "pdf" },
};

/**
 * List Drive, newest first.
 *
 * Folders are excluded and so is the trash. Without `trashed = false` a deleted
 * document keeps appearing in the browser and files perfectly well — which is the
 * kind of thing nobody notices until the day they need that document and find
 * Vault's copy of something they deliberately threw away.
 */
export async function list(opts: { q?: string; pageToken?: string } = {}): Promise<{
  files: DriveFile[];
  nextPageToken: string | null;
}> {
  const clauses = ["trashed = false", "mimeType != 'application/vnd.google-apps.folder'"];
  const term = (opts.q ?? "").trim();
  if (term) {
    // Drive's query language takes a single-quoted literal, so an apostrophe in a
    // filename ("Eben's passport") ends the string and produces a 400. Escaping is
    // a backslash before the quote.
    clauses.push(`name contains '${term.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`);
  }

  const params = new URLSearchParams({
    q: clauses.join(" and "),
    orderBy: "modifiedTime desc",
    pageSize: "50",
    fields: "nextPageToken, files(id, name, mimeType, size, modifiedTime, iconLink)",
    // Include files shared with the person, not only files they own: an insurance
    // schedule the broker shared is exactly the sort of thing Vault should hold.
    supportsAllDrives: "true",
    includeItemsFromAllDrives: "true",
  });

  const res = await driveGet(`files?${params}`);
  const body = (await res.json()) as {
    nextPageToken?: string;
    files?: Array<{ id: string; name: string; mimeType: string; size?: string; modifiedTime?: string; iconLink?: string }>;
  };

  return {
    nextPageToken: body.nextPageToken ?? null,
    files: (body.files ?? []).map((f) => ({
      id: f.id,
      name: f.name,
      mimeType: f.mimeType,
      size: f.size ? Number(f.size) : null,
      modifiedTime: f.modifiedTime ?? null,
      iconLink: f.iconLink ?? null,
      native: f.mimeType in EXPORT_AS,
    })),
  };
}

/**
 * Fetch one Drive file's bytes, ready to upload.
 *
 * Google-native files (a Doc, a Sheet) have no bytes to download — `alt=media`
 * returns a 403 for them, not a file. They have to go through `/export`, and the
 * returned name gets the exported extension so a Doc does not land in the bucket
 * as an extensionless blob that nothing will open.
 *
 * The bytes pass through the browser rather than being fetched server-side. That is
 * deliberate: the alternative is handing Vault's backend a token to the whole Drive
 * and storing a refresh token to make it durable. Doing it here means the credential
 * never leaves the tab that asked for it.
 */
export async function download(file: DriveFile): Promise<{ blob: Blob; filename: string; mime: string }> {
  const exp = EXPORT_AS[file.mimeType];
  const res = exp
    ? await driveGet(`files/${file.id}/export?mimeType=${encodeURIComponent(exp.mime)}`)
    : await driveGet(`files/${file.id}?alt=media&supportsAllDrives=true`);

  const blob = await res.blob();
  const filename = exp && !file.name.toLowerCase().endsWith(`.${exp.ext}`) ? `${file.name}.${exp.ext}` : file.name;
  return { blob, filename, mime: exp ? exp.mime : file.mimeType || "application/octet-stream" };
}
